import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';

import { ModalDirective } from 'ngx-bootstrap';

import { CBMError, Column, DatatableButton, VildVersion, VildVersionExtended } from 'app/models';
import { VildVersionStatusType } from 'app/models/enums';
import { AuthorizationService, TaskService, VildVersionService } from 'app/services';
import { DialogVildApproveComponent } from 'app/shared/dialog-vild-approve/dialog-vild-approve.component';
import { DialogVildDecodeComponent } from 'app/shared/dialog-vild-decode/dialog-vild-decode.component';
import { DialogVildDeleteComponent } from 'app/shared/dialog-vild-delete/dialog-vild-delete.component';
import { DialogVildEncodeComponent } from 'app/shared/dialog-vild-encode/dialog-vild-encode.component';
import { DateUtil } from 'app/utils';

interface CBMFileInput {
  field: string;
  accept: string;
  language: string;
}

@Component({
  selector: 'app-vildversions',
  templateUrl: './vild-version.component.html'
})
export class VildVersionComponent implements OnInit {
  @ViewChild('confirmImportVildDialog') confirmImportVildDialog: ModalDirective;

  @ViewChild('dialogVildApproveComponent') dialogVildApproveComponent: DialogVildApproveComponent;
  @ViewChild('dialogVildDeleteComponent') dialogVildDeleteComponent: DialogVildDeleteComponent;
  @ViewChild('dialogVildDecodeComponent') dialogVildDecodeComponent: DialogVildDecodeComponent;
  @ViewChild('dialogVildEncodeComponent') dialogVildEncodeComponent: DialogVildEncodeComponent;

  @ViewChildren('theFileInputs') allFileInputs: QueryList<ElementRef>;

  public vildVersions: VildVersion[] = [];
  public vildVersionToDelete: VildVersion;
  public vildVersionToApprove: VildVersion;
  public vildVersionToEncode: VildVersion;

  public columns: Column[] = [
    { property: 'id', label: 'VILDVERSION.TABLE.ID', type: 'string', flexGrow: 1.5 },
    { property: 'tableNr', label: 'VILDVERSION.TABLE.TABLE_NR', type: 'string' },
    { property: 'tableVersion', label: 'VILDVERSION.TABLE.TABLE_VERSION', type: 'string' },
    { property: 'startdate', label: 'VILDVERSION.TABLE.STARTDATE', type: 'date', flexGrow: 2.5 },
    { property: 'filename', label: 'VILDVERSION.TABLE.FILENAME', type: 'string' },
    { property: 'status', label: 'VILDVERSION.TABLE.STATUS', type: 'string' }
  ];

  public datatableButtons: DatatableButton[] = [
    {
      id: 'deleteVildVersionBtn',
      icon: 'trash',
      label: 'VILDVERSION.TABLE_BUTTON_HEADERS.DELETE',
      action: this.prepareDeleteVildVersion.bind(this),
      showIf: this.canDeleteVildVersion.bind(this)
    },
    {
      id: 'approveVildVersionBtn',
      icon: 'ok',
      label: 'VILDVERSION.TABLE_BUTTON_HEADERS.APPROVE',
      action: this.prepareApproveVildVersion.bind(this),
      showIf: this.canApproveVildVersion.bind(this)
    },
    {
      id: 'encodeVildVersionBtn',
      icon: 'refresh',
      label: 'VILDVERSION.TABLE_BUTTON_HEADERS.ENCODE',
      action: this.prepareStartVildEncoding.bind(this),
      showIf: this.canStartVildTask.bind(this)
    },
    {
      id: 'decodeVildVersionBtn',
      icon: 'refresh',
      label: 'VILDVERSION.TABLE_BUTTON_HEADERS.DECODE',
      action: this.startVildDecoding.bind(this),
      showIf: this.canStartVildTask.bind(this)
    }
  ];

  public newVildVersion: VildVersion = new VildVersion(null, null, null, null, null, null);

  public errorMessages: CBMError[] = [];

  public fileInputs: CBMFileInput[] = [
    { field: 'file_vildDbf', accept: '.dbf', language: 'VILDVERSION.EXTENDED.VILDDBF' },
    { field: 'file_pointShp', accept: '.shp', language: 'VILDVERSION.EXTENDED.TMC_POINT_SHP' },
    { field: 'file_pointShx', accept: '.shx', language: 'VILDVERSION.EXTENDED.TMC_POINT_SHX' },
    { field: 'file_pointPrj', accept: '.prj', language: 'VILDVERSION.EXTENDED.TMC_POINT_PRJ' },
    { field: 'file_pointDbf', accept: '.dbf', language: 'VILDVERSION.EXTENDED.TMC_POINT_DBF' },
    { field: 'file_lineShp', accept: '.shp', language: 'VILDVERSION.EXTENDED.TMC_LINE_SHP' },
    { field: 'file_lineShx', accept: '.shx', language: 'VILDVERSION.EXTENDED.TMC_LINE_SHX' },
    { field: 'file_linePrj', accept: '.prj', language: 'VILDVERSION.EXTENDED.TMC_LINE_PRJ' },
    { field: 'file_lineDbf', accept: '.dbf', language: 'VILDVERSION.EXTENDED.TMC_LINE_DBF' },
    { field: 'file_areaShp', accept: '.shp', language: 'VILDVERSION.EXTENDED.TMC_AREA_SHP' },
    { field: 'file_areaShx', accept: '.shx', language: 'VILDVERSION.EXTENDED.TMC_AREA_SHX' },
    { field: 'file_areaPrj', accept: '.prj', language: 'VILDVERSION.EXTENDED.TMC_AREA_PRJ' },
    { field: 'file_areaDbf', accept: '.dbf', language: 'VILDVERSION.EXTENDED.TMC_AREA_DBF' }
  ];

  public isSavingVild = false;

  constructor(
    public readonly vildVersionService: VildVersionService,
    private readonly taskService: TaskService,
    private readonly authorizationService: AuthorizationService,
    private readonly router: Router,
    private readonly dateUtil: DateUtil
  ) {}

  ngOnInit(): void {
    console.log('Retrieving all vild versions..');
    this.retrieveVildVersions();
  }

  public retrieveVildVersions(): void {
    this.vildVersionService.getVildVersions().subscribe(
      vildVersions => {
        this.vildVersions = vildVersions;
      },
      (errors: CBMError[]) => {
        this.errorMessages = errors;
      }
    );
  }

  public createVildVersion(): void {
    this.isSavingVild = true;
    this.errorMessages = [];
    this.confirmImportVildDialog.hide();

    const vildVersionExt: VildVersionExtended = this.parseVildExtendedInput();

    this.vildVersionService.newVildVersion(vildVersionExt).subscribe(
      () => {
        this.isSavingVild = false;
        this.retrieveVildVersions();
      },
      (errors: CBMError[]) => {
        this.isSavingVild = false;
        this.errorMessages = errors;
      }
    );
  }

  private isFutureVersion(vv: VildVersion): boolean {
    return new Date(vv.startdate) > new Date();
  }

  private haveFutureVildVersion(): boolean {
    return this.vildVersions !== null && this.vildVersions.some(this.isFutureVersion);
  }

  private prepareDeleteVildVersion(vildVersion: VildVersion): void {
    if (this.canDeleteVildVersion(vildVersion)) {
      this.vildVersionToDelete = vildVersion;
      this.dialogVildDeleteComponent.show();
    }
  }

  private prepareStartVildEncoding(vv: VildVersion) {
    if (this.canStartVildTask(vv)) {
      this.vildVersionToEncode = vv;
      this.dialogVildEncodeComponent.show();
    }
  }

  private startVildDecoding() {
    this.dialogVildDecodeComponent.show();
  }

  public prepareCreateVildVersion(): void {
    this.confirmImportVildDialog.show();
  }

  public stopImportVildVersion(): void {
    this.confirmImportVildDialog.hide();
  }

  private findCBMFileInputForElementByField(field: string): CBMFileInput {
    for (const fi of this.fileInputs) {
      if (fi.field === field) {
        return fi;
      }
    }
    return null;
  }

  private parseVildExtendedInput(): VildVersionExtended {
    const dateInput: string = this.dateUtil.slashesToDashes(this.newVildVersion.startdate);

    const vildVersionExt: VildVersionExtended = new VildVersionExtended(
      new VildVersion(
        null,
        this.newVildVersion.tableNr,
        this.newVildVersion.tableVersion,
        dateInput,
        this.newVildVersion.filename,
        VildVersionStatusType.uploaded
      )
    );

    this.allFileInputs.forEach((fileInput: ElementRef) => {
      const inputEl: HTMLInputElement = fileInput.nativeElement;
      if (inputEl.files.length > 0) {
        const fileInputInfo: CBMFileInput = this.findCBMFileInputForElementByField(inputEl.id);
        vildVersionExt[fileInputInfo.field] = inputEl.files.item(0);
      }
    });

    return vildVersionExt;
  }

  /**
   * Actual version is defined as 'current or future'
   * There is a maximum of 2 actual versions
   */
  private isActualVersion(vv: VildVersion): boolean {
    // assume sorted by startdate asc

    const now = new Date();
    const latestCurrent = this.vildVersions.filter(v => new Date(v.startdate) > now).reverse()[0];
    const earliestFuture = this.vildVersions.filter(v => new Date(v.startdate) < now)[0];

    return (latestCurrent && vv === latestCurrent) || (earliestFuture && vv === earliestFuture);
  }

  private goToVildMappingLine(vv: VildVersion): void {
    if (this.canEditVildMapping(vv)) {
      this.router.navigate([`/admin/vildmappingline/${vv.id}`]);
    }
  }

  private goToVildMappingPoint(vv: VildVersion): void {
    if (this.canEditVildMapping(vv)) {
      this.router.navigate([`admin/vildmappingpoint/${vv.id}`]);
    }
  }

  private goToTasks(): void {
    this.router.navigate(['admin/tasks']);
  }

  public canCreateVildVersion(): boolean {
    return this.authorizationService.isMapAdmin() && !this.haveFutureVildVersion();
  }

  private canEditVildMapping(vv: VildVersion): boolean {
    return (
      this.authorizationService.isLocationAdmin() &&
      this.isActualVersion(vv) &&
      vv.status !== VildVersionStatusType.uploaded &&
      vv.status !== VildVersionStatusType.imported
    );
  }

  private canDeleteVildVersion(vv: VildVersion): boolean {
    return this.authorizationService.isMapAdmin() && this.isFutureVersion(vv);
  }

  private canApproveVildVersion(vv: VildVersion): boolean {
    return this.authorizationService.isMapAdmin() && vv.status === VildVersionStatusType.matched;
  }

  private canStartVildTask(vv: VildVersion): boolean {
    return (
      this.authorizationService.isMapAdmin() &&
      (this.isActualVersion(vv) || this.isFutureVersion(vv)) &&
      vv.status === VildVersionStatusType.approved
    );
  }

  private prepareApproveVildVersion(vv: VildVersion): void {
    if (this.canApproveVildVersion(vv)) {
      this.vildVersionToApprove = vv;
      this.dialogVildApproveComponent.show();
    }
  }

  private onApproveOrDelete($event: boolean): void {
    if ($event) {
      this.retrieveVildVersions();
    }
  }

  private onVildTaskTriggered($event: boolean): void {
    if ($event) {
      this.goToTasks();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/vildversion/vild-version.component.ts